import {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import Storage from '../Utils/Storage';

export type AccessibilityTextSize = 'standard' | 'large' | 'extraLarge';

export type AccessibilitySettings = {
  highContrast: boolean;
  textSize: AccessibilityTextSize;
};

type AccessibilityContextValue = {
  settings: AccessibilitySettings;
  textScale: number;
  updateSettings: (nextSettings: Partial<AccessibilitySettings>) => Promise<void>;
};

export const ACCESSIBILITY_STORAGE_KEY = 'adapt-mh.accessibility.settings';
export const ACCESSIBILITY_TEXT_SIZES: AccessibilityTextSize[] = [
  'standard',
  'large',
  'extraLarge',
];

const TEXT_SCALE_BY_SIZE: Record<AccessibilityTextSize, number> = {
  standard: 1,
  large: 1.15,
  extraLarge: 1.3,
};

const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  highContrast: false,
  textSize: 'standard',
};

const normalizeSettings = (value: unknown): AccessibilitySettings => {
  if (!value || typeof value !== 'object') {
    return DEFAULT_ACCESSIBILITY_SETTINGS;
  }

  const partialSettings = value as Partial<AccessibilitySettings>;
  const textSize = ACCESSIBILITY_TEXT_SIZES.includes(
    partialSettings.textSize as AccessibilityTextSize,
  )
    ? (partialSettings.textSize as AccessibilityTextSize)
    : DEFAULT_ACCESSIBILITY_SETTINGS.textSize;

  return {
    highContrast: Boolean(partialSettings.highContrast),
    textSize,
  };
};

const AccessibilityContext = createContext<AccessibilityContextValue>({
  settings: DEFAULT_ACCESSIBILITY_SETTINGS,
  textScale: TEXT_SCALE_BY_SIZE.standard,
  updateSettings: async () => undefined,
});

export const AccessibilityProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_ACCESSIBILITY_SETTINGS);

  useEffect(() => {
    let canceled = false;

    const loadSettings = async () => {
      const rawSettings = await Storage.get(ACCESSIBILITY_STORAGE_KEY);
      if (!rawSettings || canceled) return;

      try {
        setSettings(normalizeSettings(JSON.parse(rawSettings)));
      } catch {
        setSettings(DEFAULT_ACCESSIBILITY_SETTINGS);
      }
    };

    loadSettings();

    return () => {
      canceled = true;
    };
  }, []);

  const updateSettings = useCallback(async (nextSettings: Partial<AccessibilitySettings>) => {
    setSettings((currentSettings) => {
      const mergedSettings = normalizeSettings({...currentSettings, ...nextSettings});
      Storage.set(ACCESSIBILITY_STORAGE_KEY, JSON.stringify(mergedSettings));
      return mergedSettings;
    });
  }, []);

  const value = useMemo<AccessibilityContextValue>(
    () => ({
      settings,
      textScale: TEXT_SCALE_BY_SIZE[settings.textSize],
      updateSettings,
    }),
    [settings, updateSettings],
  );

  return <AccessibilityContext.Provider value={value}>{children}</AccessibilityContext.Provider>;
};

export const useAccessibility = (): AccessibilityContextValue => {
  return useContext(AccessibilityContext);
};
